import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";

const APP_NAME = "Sentrion Plus";
const PAGE_TITLES = {
  "/": "Dashboard",
  "/leads": "Lista Lead",
  "/pipeline": "Pipeline",
  "/preventivi": "Preventivi",
  "/fascicoli": "Fascicoli clienti",
  "/pagine-vendita": "Pagine di vendita",
  "/email": "Email & Mittente",
  "/posta": "Posta",
  "/clienti": "Clienti",
  "/non-clienti": "Non Clienti",
  "/classifica": "Classifica",
  "/richiamare": "Da Contattare",
  "/calendario": "Calendario",
  "/mappa": "Mappa Clienti",
  "/itinerari": "Itinerari",
  "/catalogo": "Catalogo",
  "/statistiche-prodotti": "Statistiche Prodotti",
  "/analisi": "Analisi",
  "/rubrica": "Rubrica",
  "/impostazioni": "Impostazioni",
  "/profilo": "Il mio profilo",
  "/login": "Accedi",
  "/forgot-password": "Recupera password",
  "/reset-password": "Reimposta password",
};

function RouteTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    const name = PAGE_TITLES[pathname];
    document.title = name ? `${name} · ${APP_NAME}` : APP_NAME;
  }, [pathname]);
  return null;
}

import Login from "./pages/Login";
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Leads = lazy(() => import("./pages/Leads"));
const Pipeline = lazy(() => import("./pages/Pipeline"));
const Preventivi = lazy(() => import("./pages/Preventivi"));
const Fascicoli = lazy(() => import("./pages/Fascicoli"));
const Documenti = lazy(() => import("./pages/Documenti"));
const PagineVendita = lazy(() => import("./pages/PagineVendita"));
const PublicLanding = lazy(() => import("./pages/PublicLanding"));
const PublicProduct = lazy(() => import("./pages/PublicProduct"));
const MittenteEmail = lazy(() => import("./pages/MittenteEmail"));
const Clienti = lazy(() => import("./pages/Clienti"));
const NonClienti = lazy(() => import("./pages/NonClienti"));
const Classifica = lazy(() => import("./pages/Classifica"));
const PeggioriClienti = lazy(() => import("./pages/PeggioriClienti"));
const RadarAI = lazy(() => import("./pages/RadarAI"));
const Collaboratori = lazy(() => import("./pages/Collaboratori"));
const Messaggi = lazy(() => import("./pages/Messaggi"));
const Posta = lazy(() => import("./pages/Posta"));
const Reminders = lazy(() => import("./pages/Reminders"));
const Calendario = lazy(() => import("./pages/Calendario"));
const MapView = lazy(() => import("./pages/MapView"));
const Catalogo = lazy(() => import("./pages/Catalogo"));
const StatisticheProdotti = lazy(() => import("./pages/StatisticheProdotti"));
const Analisi = lazy(() => import("./pages/Analisi"));
const Rubrica = lazy(() => import("./pages/Rubrica"));
const Itinerari = lazy(() => import("./pages/Itinerari"));
const Settings = lazy(() => import("./pages/Settings"));
const ProfiloUtente = lazy(() => import("./pages/ProfiloUtente"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
    </div>
  );
}

function usePrefetchRoutes() {
  useEffect(() => {
    const prefetch = () => {
      // Precarica in background i chunk delle pagine più usate → navigazione istantanea
      import("./pages/Dashboard");
      import("./pages/Leads");
      import("./pages/Pipeline");
      import("./pages/Clienti");
      import("./pages/Posta");
      import("./pages/Preventivi");
      import("./pages/Catalogo");
      import("./pages/RadarAI");
      import("./pages/Rubrica");
      import("./pages/Calendario");
    };
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
    const id = ric(prefetch);
    return () => (window.cancelIdleCallback || clearTimeout)(id);
  }, []);
}

function App() {
  usePrefetchRoutes();
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <RouteTitle />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/p/:slug" element={<PublicLanding />} />
            <Route path="/prodotto/:slug" element={<PublicProduct />} />
            <Route
              path="/pagine-vendita"
              element={
                <ProtectedRoute>
                  <PagineVendita />
                </ProtectedRoute>
              }
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  <Leads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pipeline"
              element={
                <ProtectedRoute>
                  <Pipeline />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documenti"
              element={
                <ProtectedRoute>
                  <Documenti />
                </ProtectedRoute>
              }
            />
            <Route
              path="/preventivi"
              element={
                <ProtectedRoute>
                  <Preventivi />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fascicoli"
              element={
                <ProtectedRoute page="fascicoli">
                  <Fascicoli />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email"
              element={
                <ProtectedRoute>
                  <MittenteEmail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clienti"
              element={
                <ProtectedRoute page="clienti">
                  <Clienti />
                </ProtectedRoute>
              }
            />
            <Route
              path="/non-clienti"
              element={
                <ProtectedRoute page="non_clienti">
                  <NonClienti />
                </ProtectedRoute>
              }
            />
            <Route
              path="/classifica"
              element={
                <ProtectedRoute>
                  <Classifica />
                </ProtectedRoute>
              }
            />
            <Route
              path="/peggiori-clienti"
              element={
                <ProtectedRoute>
                  <PeggioriClienti />
                </ProtectedRoute>
              }
            />
            <Route
              path="/radar-ai"
              element={
                <ProtectedRoute>
                  <RadarAI />
                </ProtectedRoute>
              }
            />
            <Route
              path="/collaboratori"
              element={
                <ProtectedRoute>
                  <Collaboratori />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messaggi"
              element={
                <ProtectedRoute>
                  <Messaggi />
                </ProtectedRoute>
              }
            />
            <Route
              path="/posta"
              element={
                <ProtectedRoute>
                  <Posta />
                </ProtectedRoute>
              }
            />
            <Route
              path="/richiamare"
              element={
                <ProtectedRoute>
                  <Reminders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendario"
              element={
                <ProtectedRoute>
                  <Calendario />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mappa"
              element={
                <ProtectedRoute page="mappa">
                  <MapView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/itinerari"
              element={
                <ProtectedRoute page="itinerari">
                  <Itinerari />
                </ProtectedRoute>
              }
            />
            <Route
              path="/catalogo"
              element={
                <ProtectedRoute>
                  <Catalogo />
                </ProtectedRoute>
              }
            />
            <Route
              path="/statistiche-prodotti"
              element={
                <ProtectedRoute>
                  <StatisticheProdotti />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analisi"
              element={
                <ProtectedRoute>
                  <Analisi />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rubrica"
              element={
                <ProtectedRoute>
                  <Rubrica />
                </ProtectedRoute>
              }
            />
            <Route
              path="/impostazioni"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profilo"
              element={
                <ProtectedRoute>
                  <ProfiloUtente />
                </ProtectedRoute>
              }
            />
          </Routes>
          </Suspense>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
